/* eslint-disable max-classes-per-file, react/no-multi-comp */
// Multiple classes and components used for HOC
/**
 * @typedef {import('../../shared/PropertyService/Comparables').default} Comps;
 * @typedef {import('./PropertyContext').default} Context
 * @typedef {import('./Props').default} Props
 * @typedef {import('./State').default} State
 * @typedef {import('./State').Property} Property
 */
import { AuthContext } from '../../../shared/AuthProvider';
import { getComparables } from '../homeowner-service';
import { selectedPropertyStorageKey } from '../../../config/local/properties';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';
import { translate } from '../../Internationalization';
import {
	withHomeownerService,
	withPropertyService,
} from '../../../service-container';
import captureError from '../../../utils/captureError';
import images from '../../../config/local/images';
import LoadingScreen from '../../../shared/LoadingScreen';
import React from 'react';

/** @type {React.Context<Context>} */
export const PropertyContext = React.createContext(
	/** @type {Context} */ ({
		agentId: null,
		agentName: null,
		comparables: {
			comparables: [],
			listed: [],
			median: {
				listed: null,
				sold: null,
			},
			sold: [],
		},
		propertyImage: null,
		propertyList: [],
		refreshPropertyList: () => Promise.resolve(),
		selectedProperty: null,
		selectedPropertyDetails: null,
		setSelectedProperty: () => Promise.resolve(),
	})
);

/**
 * @param {Property[]} propertyList
 * @param {string | null | undefined} propertyId
 * @returns {Property | undefined}
 */
function resolveSelectedProperty(propertyList, propertyId) {
	if (propertyId) {
		const matchedProperty = propertyList.find(
			(property) => property.id === propertyId
		);

		if (matchedProperty) {
			return matchedProperty;
		}
	}

	return (
		propertyList.find((property) => property.isPrimary === true) ??
		propertyList.find(Boolean)
	);
}

/** @extends {React.Component<Props, State>} */
class PropertyProvider extends React.Component {
	#storageId = selectedPropertyStorageKey;
	/** @type {string | null} */
	#comparablesForPropertyId = null;
	/** @type {string | null} */
	#comparablesRequestForPropertyId = null;

	/** @type {import('../../../shared/AuthProvider/State').default | null} */
	context = null;

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			agentId: null,
			agentName: null,
			comparables: {
				comparables: [],
				listed: [],
				median: {
					listed: null,
					sold: null,
				},
				sold: [],
			},
			errorMessage: null,
			isGettingData: true,
			propertyImage: undefined,
			propertyList: [],
			refreshPropertyList: this.refreshPropertyList.bind(this),
			selectedProperty: null,
			selectedPropertyDetails: null,
			setSelectedProperty: this.setSelectedProperty.bind(this),
		};
	}

	/**
	 * @param {unknown} error
	 * @returns {void}
	 */
	#error(error) {
		captureError(error);

		this.setState({ errorMessage: translate('global.error') });
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		try {
			const [propertyList, agent] = await Promise.all([
				this.getPropertyList(),
				this.getAgentInfo(),
			]);
			const selectedProperty = this.getSelectedProperty(propertyList);
			const propertyImage = await this.getPropertyImage(selectedProperty);

			this.setState((state) => ({
				...state,
				agentId: agent.id,
				agentName: agent.name,
				isGettingData: false,
				propertyImage,
				propertyList,
				selectedProperty: selectedProperty ?? null,
			}));
		} catch (error) {
			this.#error(error);
			this.setState({ isGettingData: false });
		}
	}

	/**
	 * @param {Props} prevProps
	 * @param {State} prevState
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps, prevState) {
		const propertyList = await this.getPropertyList();

		const selectedProperty = propertyList?.filter(
			(property) => property.id === this.state.selectedProperty?.id
		)[0];

		/*
		 * This checks if the selected property data returned by the API is the
		 * same as the selected property data currently in state.
		 */
		if (
			JSON.stringify(prevState?.selectedProperty) !==
			JSON.stringify(selectedProperty)
		) {
			this.setState({
				isGettingData: false,
				propertyList,
				selectedProperty,
			});
		}

		if (propertyList?.length !== prevState.propertyList?.length) {
			this.setState((state) => ({
				...state,
				isGettingData: false,
				propertyList,
			}));
		}

		if (
			this.state.selectedProperty?.id &&
			prevState?.selectedProperty?.id !== this.state.selectedProperty?.id
		) {
			await this.loadSelectedPropertyResources(
				this.state.selectedProperty.id
			);
		}
	}

	/**
	 * @param {string | undefined} propertyId
	 * @returns {Promise<void>}
	 */
	async loadSelectedPropertyResources(propertyId) {
		if (!propertyId) {
			this.setState({
				comparables: {
					comparables: [],
					listed: [],
					median: {
						listed: null,
						sold: null,
					},
					sold: [],
				},
				isGettingData: false,
				propertyImage: toAbsoluteUrl(this.defaultImage),
				selectedPropertyDetails: null,
			});
			return;
		}

		if (
			this.#comparablesForPropertyId === propertyId ||
			this.#comparablesRequestForPropertyId === propertyId
		) {
			return;
		}

		const accessToken = this.context?.accessToken;

		if (!accessToken) {
			this.setState({ isGettingData: false });
			return;
		}

		this.#comparablesRequestForPropertyId = propertyId;

		try {
			const [comparables, propertyImage, selectedPropertyDetails] =
				await Promise.all([
					getComparables(accessToken, propertyId),
					this.props.propertyService.getDefaultImage(propertyId),
					this.props.propertyService
						.getEditablePropertyById(propertyId)
						.catch(() => null),
				]);

			this.#comparablesForPropertyId = propertyId;

			this.setState({
				comparables,
				isGettingData: false,
				propertyImage: toAbsoluteUrl(
					propertyImage ?? this.defaultImage
				),
				selectedPropertyDetails,
			});
		} catch (error) {
			this.#error(error);
			this.setState({
				isGettingData: false,
				propertyImage: toAbsoluteUrl(this.defaultImage),
				selectedPropertyDetails: null,
			});
		} finally {
			this.#comparablesRequestForPropertyId = null;
		}
	}

	/**
	 * @returns {Promise<Property[]>}
	 */
	async getPropertyList() {
		try {
			/*
			 * TODO: This prop is often used to set the state. Using a prop to
			 * set state has unforeseen consequences.
			 *
			 * Ref: https://reactjs.org/docs/state-and-lifecycle.html#state-updates-may-be-asynchronous
			 */
			return this.props.propertyService.getHomeownerProperties();
		} catch (error) {
			/*
			 * TODO: This setting the state is can lead to unforeseen consequences
			 * as this is using props to set state and this method is called
			 * frequently to set the state.
			 */
			this.#error(error);
		}

		return [];
	}

	/** @returns {Promise<{ id: string | null, name: string | null }>} */
	async getAgentInfo() {
		try {
			const agent = await this.props.homeownerService.getHomeownerAgent();

			return {
				id: agent?.id ?? null,
				name: agent?.name ?? null,
			};
		} catch (error) {
			captureError(error);
		}

		return {
			id: null,
			name: null,
		};
	}

	defaultImage = images.homeowners.properties.streetView;

	/**
	 * @param {Property | null | undefined} property
	 * @returns {Promise<string>}
	 */
	async getPropertyImage(property) {
		let propertyImage;

		if (!property?.id) {
			return toAbsoluteUrl(this.defaultImage);
		}

		try {
			propertyImage = await this.props.propertyService.getDefaultImage(
				property.id
			);
		} catch (error) {
			captureError(error);
		}

		return toAbsoluteUrl(propertyImage ?? this.defaultImage);
	}

	/**
	 * @param {Property[]} propertyList
	 * @returns {Property | undefined}
	 */
	getSelectedProperty(propertyList) {
		const storedId = localStorage.getItem(this.#storageId);

		return resolveSelectedProperty(propertyList, storedId);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async refreshPropertyList() {
		const propertyList = await this.getPropertyList();
		const storedId = localStorage.getItem(this.#storageId);

		this.setState((prevState) => {
			const selected =
				resolveSelectedProperty(
					propertyList,
					prevState.selectedProperty?.id
				) ??
				resolveSelectedProperty(propertyList, storedId) ??
				null;

			if (selected?.id) {
				window.localStorage.setItem(this.#storageId, selected.id);
			}

			return {
				...prevState,
				isGettingData: false,
				propertyList,
				selectedProperty: selected,
			};
		});
	}

	/**
	 * @param {string} id
	 * @param {boolean} [flag]
	 * @returns {Promise<void>}
	 */
	async setSelectedProperty(id, flag = false) {
		const refreshed = flag ? await this.getPropertyList() : null;

		this.setState((prevState) => {
			const propertyList = refreshed ?? prevState.propertyList;

			const selected = propertyList.find(
				(property) => property.id === id
			);

			if (selected === undefined) {
				return { ...prevState };
			}

			if (prevState.selectedProperty?.id !== selected.id) {
				this.#comparablesForPropertyId = null;
				this.#comparablesRequestForPropertyId = null;
			}

			window.localStorage.setItem(this.#storageId, selected.id);

			return {
				...prevState,
				isGettingData: false,
				propertyList,
				selectedProperty: selected,
				selectedPropertyDetails: null,
			};
		});
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		/** @type {React.ReactNode} */
		let content = this.props.children;

		if (this.state.isGettingData) {
			content = <LoadingScreen />;
		}

		return (
			<PropertyContext.Provider
				value={{
					agentId: this.state.agentId,
					agentName: this.state.agentName,
					comparables: this.state.comparables,
					propertyImage: this.state.propertyImage,
					propertyList: this.state.propertyList,
					refreshPropertyList: this.state.refreshPropertyList,
					selectedProperty: this.state.selectedProperty,
					selectedPropertyDetails: this.state.selectedPropertyDetails,
					setSelectedProperty: this.state.setSelectedProperty,
				}}
			>
				{content}
			</PropertyContext.Provider>
		);
	}
}

PropertyProvider.contextType = AuthContext;

// @ts-ignore TODO: Remove when service container types are fixed
export default withPropertyService(withHomeownerService(PropertyProvider));

/**
 * @template {object} TProps
 * @param {React.ComponentType<TProps & import('./PropertyContext').PropType>} Component
 * @returns {React.ComponentType<TProps>}
 */
export function withProperty(Component) {
	class PropertyComponent extends React.Component {
		render() {
			const property = /** @type {Context} */ (this.context);
			const props = /** @type {TProps} */ (this.props);

			/* eslint-disable-next-line react/jsx-props-no-spreading -- necessary for HOC */
			return <Component property={property} {...props} />;
		}
	}

	PropertyComponent.contextType = PropertyContext;

	return PropertyComponent;
}
